import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Chart from "react-apexcharts";
import { ApexOptions } from 'apexcharts';
import LoadingData from './LoadingData';
import { Box } from '@mui/material';
import { createDateTimeChartConfig, createYAxisConfig, ChartColors } from './shared/ChartConfig';
import ColorGuide from './ColorGuide';

interface RateDataItem {
  timestamp: string;
  rate: number;
  rate_delta?: number;
}

interface ElectricityRatesChartProps { 
  loading: boolean;
  data: RateDataItem[];
  tariffInfo?: string;
  showDeltaChart?: boolean;
  isAgile?: boolean;
  'data-chart-id'?: string;
}

// Helper function to find 2-hour periods
const findTwoHourPeriods = (timestamps: Date[], rates: number[]) => {
  const periods: {start: Date, end: Date, averageRate: number}[] = [];
  const durationMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  for (let i = 0; i < timestamps.length - 1; i++) {
    const startTime = timestamps[i];
    const targetEndTime = new Date(startTime.getTime() + durationMs);
    
    // Find the last data point within this period
    let lastIndex = i;
    for (let j = i + 1; j < timestamps.length; j++) {
      const currentTime = timestamps[j];
      if (currentTime < targetEndTime) {
        lastIndex = j;
      } else {
        break;
      }
    }

    // Calculate average rate for this period
    const periodData = rates.slice(i, lastIndex + 1);
    const averageValue = periodData.reduce((sum, rate) => sum + rate, 0) / periodData.length;

    periods.push({
      start: startTime,
      end: targetEndTime,
      averageRate: averageValue
    });
  }

  return periods;
};

const ElectricityRatesChart: React.FC<ElectricityRatesChartProps> = ({ loading, data, tariffInfo, showDeltaChart = true, isAgile = false, 'data-chart-id': dataChartId}) => {
  const [xData, setXData] = React.useState<Date[]>([]);
  const [rateData, setRateData] = React.useState<number[]>([]);
  const [deltaData, setDeltaData] = React.useState<number[]>([]);
  const [hasDeltaData, setHasDeltaData] = React.useState<boolean>(false);
  const [currentPrice, setCurrentPrice] = React.useState<number>(0);
  const [nextPrice, setNextPrice] = React.useState<number>(0);
  const [nowTime, setNowTime] = React.useState<Date>(new Date());
  const [minTimestamp, setMinTimestamp] = React.useState<number>(0);
  const [maxTimestamp, setMaxTimestamp] = React.useState<number>(0);
  const [cheapestPeriod, setCheapestPeriod] = React.useState<{start: Date, end: Date, rate: number} | null>(null);
  const [mostExpensivePeriod, setMostExpensivePeriod] = React.useState<{start: Date, end: Date, rate: number} | null>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    if (data.length > 0) {
      const timestamps = data.map(item => new Date(item.timestamp));
      const rates = data.map(item => item.rate);
      const deltas = data.map(item => item.rate_delta || 0);
      
      // Check if we have delta data
      const hasDelta = data.some(item => item.rate_delta !== undefined);
      
      setXData(timestamps);
      setRateData(rates);
      setDeltaData(deltas);
      setHasDeltaData(hasDelta);
      setNowTime(new Date());
      
      // Calculate min/max timestamps from data
      const minTime = Math.min(...timestamps.map(t => t.getTime()));
      const maxTime = Math.max(...timestamps.map(t => t.getTime()));
      setMinTimestamp(minTime);
      setMaxTimestamp(maxTime);
      
      // Find current and next prices
      const now = new Date();
      let currentPrice = 0;
      let nextPrice = 0;
      
      for (let i = 0; i < timestamps.length; i++) {
        const timestamp = timestamps[i];
        const nextTimestamp = i < timestamps.length - 1 ? timestamps[i + 1] : null;
        
        // Current price: rate for the period that contains "now"
        if (now >= timestamp && (!nextTimestamp || now < nextTimestamp)) {
          currentPrice = rates[i];
        }
        
        // Next price: rate for the next period after current
        if (now < timestamp && nextPrice === 0) {
          nextPrice = rates[i];
          break;
        }
      }
      
      setCurrentPrice(currentPrice);
      setNextPrice(nextPrice);

      // Find cheapest and most expensive 2-hour periods
      const twoHourPeriods = findTwoHourPeriods(timestamps, rates);
      if (twoHourPeriods.length > 0) {
        const cheapest = twoHourPeriods.reduce((min, period) => 
          period.averageRate < min.averageRate ? period : min
        );
        const mostExpensive = twoHourPeriods.reduce((max, period) => 
          period.averageRate > max.averageRate ? period : max
        );
        
        setCheapestPeriod({
          start: cheapest.start,
          end: cheapest.end,
          rate: cheapest.averageRate
        });
        setMostExpensivePeriod({
          start: mostExpensive.start,
          end: mostExpensive.end,
          rate: mostExpensive.averageRate
        });
      } else {
        setCheapestPeriod(null);
        setMostExpensivePeriod(null);
      }
    } else {
      setXData([]);
      setRateData([]);
      setDeltaData([]);
      setHasDeltaData(false);
      setCurrentPrice(0);
      setNextPrice(0);
      setMinTimestamp(0);
      setMaxTimestamp(0);
      setCheapestPeriod(null);
      setMostExpensivePeriod(null);
    }
  }, [data]);

  const getColorForRate = (rate: number, timestamp: Date) => {
    const now = new Date();
    
    // Historical rates: grey out only when the period has ended
    // Each period is 30 minutes, so add 30 minutes to get the end time
    const periodEnd = new Date(timestamp.getTime() + 30 * 60 * 1000);
    if (now >= periodEnd) {
      return '#808080'; // Grey for completed historical rates
    }
    
    // Future rates use color thresholds
    if (rate < 0) return '#6666ff';      // Blue for negative rates
    if (rate < 10) return '#00cc00';     // Green for low rates
    if (rate < 20) return '#ffeb00';     // Yellow for medium-low rates
    if (rate < 30) return '#ffa500';     // Orange for medium-high rates
    if (rate < 40) return '#ff0000';     // Red for high rates
    return '#800080';                    // Purple for very high rates
  };

  const electricityRateColorGuide = [
    { color: '#6666ff', label: 'Negative' },
    { color: '#00cc00', label: 'V. Low' },
    { color: '#ffeb00', label: 'Low' },
    { color: '#ffa500', label: 'Moderate' },
    { color: '#ff0000', label: 'High' },
    { color: '#800080', label: 'V. High' }
  ];

  const electricityRateDeltaColorGuide = [
    { color: '#6666ff', label: 'Much cheaper' },
    { color: '#00cc00', label: 'Cheaper' },
    { color: '#ffeb00', label: 'A little cheaper' },
    { color: '#ffa500', label: 'A little more expensive' },
    { color: '#ff0000', label: 'More expensive' },
    { color: '#800080', label: 'Much more expensive' }
  ];

  const getColorForDelta = (delta: number, rate: number) => {
    if (delta === 0) return '#808080'; // Grey for no change
    
    const percentage = Math.abs(delta / rate) * 100;
    
    if (delta > 0) {
      // Positive delta (current rate higher than average)
      if (percentage < 10) return '#ffa500';    // Orange
      if (percentage < 25) return '#ff0000';   // Red
      return '#800080';                        // Purple
    } else {
      // Negative delta (current rate lower than average)
      if (percentage < 10) return '#ffeb00';    // Yellow
      if (percentage < 25) return '#00cc00';    // Green
      return '#6666ff';                        // Blue
    }
  };

  const options: ApexOptions = {
    ...createDateTimeChartConfig({
      theme,
      chartType: 'bar',
      minTimestamp,
      maxTimestamp,
      nowTime,
      showNowMarker: true,
      tickAmount: 8,
      isMobile
    }),
    colors: ChartColors.electricityRates,
    chart: {
      ...createDateTimeChartConfig({
        theme,
        chartType: 'bar',
        minTimestamp,
        maxTimestamp,
        nowTime,
        showNowMarker: true,
        tickAmount: 8,
        isMobile
      }).chart,
      zoom: {
        enabled: false
      }
    },
    yaxis: createYAxisConfig(theme, 'Cost (p / kWh)', {
      min: Math.min(0, ...rateData),
      max: Math.max(40, ...rateData),
      tickAmount: 4,
      decimalsInFloat: 0,
      isMobile
    }),
    tooltip: {
      theme: theme.palette.mode,
      x: {
        formatter: function(value: number) {
          const date = new Date(value - (15 * 60 * 1000)); // Add back 15 minutes to show original timestamp
          return date.toLocaleString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
      },
      y: {
        formatter: function(value: number) {
          return `${value.toFixed(1)} p/kWh`;
        }
      }
    }
  };

  const series = [
    {
      name: "Electricity Rate",
      type: "bar",
      data: rateData.map((rate, index) => ({
        x: xData[index].getTime() + (15 * 60 * 1000), // Shift visual position by -15 minutes
        y: rate,
        fillColor: getColorForRate(rate, xData[index]) // Use original timestamp for color logic
      }))
    }
  ];

  // Delta chart options and series
  const deltaOptions: ApexOptions = {
    ...createDateTimeChartConfig({
      theme,
      chartType: 'bar',
      minTimestamp,
      maxTimestamp,
      nowTime,
      showNowMarker: true,
      tickAmount: 8,
      isMobile
    }),
    colors: ChartColors.electricityRates,
    chart: {
      ...createDateTimeChartConfig({
        theme,
        chartType: 'bar',
        minTimestamp,
        maxTimestamp,
        nowTime,
        showNowMarker: true,
        tickAmount: 8,
        isMobile
      }).chart,
      zoom: {
        enabled: false
      }
    },
    yaxis: createYAxisConfig(theme, '+/- average', {
      min: -Math.max(Math.abs(Math.min(0, ...deltaData)), Math.abs(Math.max(0, ...deltaData))),
      max: Math.max(Math.abs(Math.min(0, ...deltaData)), Math.abs(Math.max(0, ...deltaData))),
      tickAmount: 2,
      decimalsInFloat: 0,
      isMobile
    }),
    tooltip: {
      theme: theme.palette.mode,
      x: {
        formatter: function(value: number) {
          const date = new Date(value - (15 * 60 * 1000)); // Add back 15 minutes to show original timestamp
          return date.toLocaleString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
      },
      y: {
        formatter: function(value: number) {
          return `${value > 0 ? '+' : ''}${value.toFixed(1)} p/kWh`;
        }
      }
    }
  };

  const deltaSeries = [
    {
      name: "Difference compared to normal",
      type: "bar",
      data: deltaData.map((delta, index) => ({
        x: xData[index].getTime() + (15 * 60 * 1000), // Shift visual position by -15 minutes
        y: delta,
        fillColor: getColorForDelta(delta, rateData[index])
      }))
    }
  ];

  return (
    <Card sx={{ width: '100%', position: 'relative', backgroundColor: theme.palette.background.paper }}>
      <CardContent>
        <LoadingData open={loading} />
        <Box sx={{ px: { xs: 1, md: 0 } }}>
          {/* Tariff Name Header */}
          {tariffInfo && (
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
              {tariffInfo}
            </Typography>
          )}
          
          {/* Current and Next Price Display */}
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" color="text.secondary" sx={{ fontSize: '14px' }}>
                Current Electricity Price
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 'bold',
                  color: getColorForRate(currentPrice, new Date()),
                  fontSize: '24px'
                }}
              >
                {currentPrice.toFixed(1)} p/kWh
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" color="text.secondary" sx={{ fontSize: '14px' }}>
                Next Electricity Price
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 'bold',
                  color: getColorForRate(nextPrice, new Date(Date.now() + 30 * 60 * 1000)),
                  fontSize: '24px'
                }}
              >
                {nextPrice.toFixed(1)} p/kWh
              </Typography>
            </Box>
          </Stack>        
        </Box>
        <Box sx={{ mt: 2 }}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height={225}
            data-chart-id={dataChartId}
          />
          
          <ColorGuide 
            items={electricityRateColorGuide}
          />
          
          {/* Delta Chart - only show if delta data exists and showDeltaChart is true */}
          {hasDeltaData && showDeltaChart && (
            <>
              <Box sx={{ mt: -3 }}>
                <Chart
                  options={deltaOptions}
                  series={deltaSeries}
                  type="bar"
                  height={150}
                  data-chart-id={`${dataChartId}-delta`}
                />
              </Box>
              <ColorGuide 
                items={electricityRateDeltaColorGuide}
              />
            </>
          )}
          
          
          {/* Cheapest and Most Expensive Period Cards */}
          {(isAgile && (cheapestPeriod || mostExpensivePeriod)) && (
            <Box sx={{mx: {xs: 1, md: 0}}}>
              <Typography variant="h5" sx={{ mt: 0, mb: 2 }}>Cheapest and Most Expensive 2-hour periods</Typography>
              <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
                {cheapestPeriod && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{
                      backgroundColor: getColorForRate(cheapestPeriod.rate, cheapestPeriod.start),
                      color: getColorForRate(cheapestPeriod.rate, cheapestPeriod.start) === '#ffeb00' ? 'black' : 'white',
                      textAlign: 'center'
                    }}>
                      <CardContent sx={{ 
                        py: 1.5, 
                        px: '0 !important'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {cheapestPeriod.rate.toFixed(1)} p/kWh from {cheapestPeriod.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} to {cheapestPeriod.end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {mostExpensivePeriod && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{
                      backgroundColor: getColorForRate(mostExpensivePeriod.rate, mostExpensivePeriod.start),
                      color: getColorForRate(mostExpensivePeriod.rate, mostExpensivePeriod.start) === '#ffeb00' ? 'black' : 'white',
                      textAlign: 'center'
                    }}>
                      <CardContent sx={{ 
                        py: 1.5, 
                        px: '0 !important'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {mostExpensivePeriod.rate.toFixed(1)} p/kWh from {mostExpensivePeriod.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} to {mostExpensivePeriod.end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ElectricityRatesChart;
